import { create } from 'zustand';
import { adminCustomerManagementApi, adminOrganizationManagementApi, adminProductDetailApi } from '@api/api-config.ts';
import {
  AdminOrganizationDetailResponse,
  AdminCustomerAuctionResponse,
  AdminCustomerProductResponse,
  AdminCustomerPriceResponse,
  AdminCreateCustomerPriceRequest,
  AdminUpdatePriceVariantRequest,
  PriceType,
} from '@api/generated/portal-be-admin';

interface PaginationState {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface PriceParams {
  page?: number;
  size?: number;
  q?: string;
  supplierId?: number;
  auctionId?: number;
  priceType?: PriceType;
  isGuaranteed?: boolean;
}

interface AdminCustomerDetailStoreState {
  customer: AdminOrganizationDetailResponse | null;
  loading: boolean;
  error: string | null;

  auctions: AdminCustomerAuctionResponse[];
  auctionsLoading: boolean;
  auctionsPagination: PaginationState | null;

  products: AdminCustomerProductResponse[];
  productsLoading: boolean;
  productsPagination: PaginationState | null;

  prices: AdminCustomerPriceResponse[];
  pricesLoading: boolean;
  pricesPagination: PaginationState | null;

  loadCustomerDetail: (customerId: number) => Promise<void>;
  loadAuctions: (customerId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  loadProducts: (customerId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  loadPrices: (customerId: number, params: PriceParams) => Promise<void>;

  createPrice: (customerId: number, request: AdminCreateCustomerPriceRequest) => Promise<void>;
  updatePriceVariant: (productId: number, priceId: number, variantId: number, request: AdminUpdatePriceVariantRequest) => Promise<void>;
  removeFromCustomer: (productId: number, priceId: number, visibilityId: number) => Promise<void>;

  reset: () => void;
}

const toPagination = (r: { page: number; size: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean }): PaginationState => ({
  page: r.page,
  size: r.size,
  total: r.total,
  totalPages: r.totalPages,
  hasNext: r.hasNext,
  hasPrev: r.hasPrev,
});

export const useAdminCustomerDetailStore = create<AdminCustomerDetailStoreState>(set => ({
  customer: null,
  loading: false,
  error: null,

  auctions: [],
  auctionsLoading: false,
  auctionsPagination: null,

  products: [],
  productsLoading: false,
  productsPagination: null,

  prices: [],
  pricesLoading: false,
  pricesPagination: null,

  loadCustomerDetail: async (customerId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await adminOrganizationManagementApi.getOrganizationDetail({ id: customerId });
      set({ loading: false, error: null, customer: response.data });
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : 'Chyba při načítání zákazníka', customer: null });
    }
  },

  loadAuctions: async (customerId: number, params) => {
    try {
      set({ auctionsLoading: true });
      const response = await adminCustomerManagementApi.getCustomerAuctions({ customerId, page: params.page || 1, size: params.size || 20, q: params.q });
      set({ auctionsLoading: false, auctions: response.data.items, auctionsPagination: toPagination(response.data) });
    } catch {
      set({ auctionsLoading: false, auctions: [], auctionsPagination: null });
    }
  },

  loadProducts: async (customerId: number, params) => {
    try {
      set({ productsLoading: true });
      const response = await adminCustomerManagementApi.getCustomerProducts({ customerId, page: params.page || 1, size: params.size || 20, q: params.q });
      set({ productsLoading: false, products: response.data.items, productsPagination: toPagination(response.data) });
    } catch {
      set({ productsLoading: false, products: [], productsPagination: null });
    }
  },

  loadPrices: async (customerId: number, params: PriceParams) => {
    try {
      set({ pricesLoading: true });
      const response = await adminCustomerManagementApi.getCustomerPrices({
        customerId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
        supplierId: params.supplierId,
        auctionId: params.auctionId,
        priceType: params.priceType,
        isGuaranteed: params.isGuaranteed,
      });
      set({ pricesLoading: false, prices: response.data.items, pricesPagination: toPagination(response.data) });
    } catch {
      set({ pricesLoading: false, prices: [], pricesPagination: null });
    }
  },

  createPrice: async (customerId, request) => {
    await adminCustomerManagementApi.createCustomerProductPrice({ customerId, adminCreateCustomerPriceRequest: request });
  },

  updatePriceVariant: async (productId, priceId, variantId, request) => {
    await adminProductDetailApi.updateProductPriceVariant({ productId, priceId, variantId, adminUpdatePriceVariantRequest: request });
  },

  removeFromCustomer: async (productId, priceId, visibilityId) => {
    await adminProductDetailApi.deletePriceVisibility({ productId, priceId, visibilityId });
  },

  reset: () => {
    set({
      customer: null,
      loading: false,
      error: null,
      auctions: [],
      auctionsLoading: false,
      auctionsPagination: null,
      products: [],
      productsLoading: false,
      productsPagination: null,
      prices: [],
      pricesLoading: false,
      pricesPagination: null,
    });
  },
}));
